import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Switch,
  TablePagination,
  TextField,
  Box,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { License, LicenseENUM, ToolENUM, LicenseInput } from '../../types/license';
import { licenseService } from '../../services/licenseService';
import debounce from 'lodash/debounce';
import { CreateLicenseForm } from './CreateLicenseForm';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, PageHeader, StatusChip } from '../ui';

export const LicenseList: React.FC = () => {
  const location = useLocation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const [licenses, setLicenses] = useState<License[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentModel, setCurrentModel] = useState<ToolENUM>(
    location.pathname.includes('michanger') ? ToolENUM.MiChanger : ToolENUM.OneChanger
  );
  const [newLicense, setNewLicense] = useState<LicenseInput>({
    email: '',
    serialNo: '',
    durationInDay: 30,
    durationDayCustom: 0,
    type: LicenseENUM.BASIC,
    dateStart: Date.now(),
    model: currentModel
  });
  const [isLoading, setIsLoading] = useState(false);
  const [extensionDialog, setExtensionDialog] = useState<{
    open: boolean;
    remainingDays: number;
    license: LicenseInput | null;
  }>({
    open: false,
    remainingDays: 0,
    license: null
  });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    license: LicenseInput | null;
  }>({
    open: false,
    license: null
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [transferDialog, setTransferDialog] = useState<{
    open: boolean;
    sourceLicense: License | null;
    destEmail: string;
    destSerialNo: string;
  }>({
    open: false,
    sourceLicense: null,
    destEmail: '',
    destSerialNo: ''
  });
  const [transferLoading, setTransferLoading] = useState(false);

  // Update currentModel when location changes
  useEffect(() => {
    const newModel = location.pathname.includes('michanger') ? ToolENUM.MiChanger : ToolENUM.OneChanger;
    setCurrentModel(newModel);
    // Clear search term when switching between tools
    setSearchTerm('');
    setPage(0);
  }, [location.pathname]);

  // Update newLicense model when currentModel changes
  useEffect(() => {
    setNewLicense(prev => ({
      ...prev,
      model: currentModel
    }));
  }, [currentModel]);

  const fetchLicenses = useCallback(async (currentPage: number, currentRowsPerPage: number, search?: string, status?: string) => {
    setIsLoading(true);
    try {
      const response = await licenseService.getLicenses({
        limit: currentRowsPerPage,
        page: currentPage,
        searchTerm: search,
        status: status,
        model: currentModel
      });

      if (response.licenses) {
        setLicenses(response.licenses);
        setTotalCount(response.total || 0);
        const calculatedTotalPages = Math.ceil((response.total || 0) / currentRowsPerPage);
        setTotalPages(calculatedTotalPages);

        if (currentPage >= calculatedTotalPages && calculatedTotalPages > 0) {
          setPage(calculatedTotalPages - 1);
          const lastPageResponse = await licenseService.getLicenses({
            limit: currentRowsPerPage,
            page: calculatedTotalPages - 1,
            searchTerm: search,
            status: status,
            model: currentModel
          });
          if (lastPageResponse.licenses) {
            setLicenses(lastPageResponse.licenses);
          }
        }
      }
    } catch (error) {
      setError('Failed to fetch licenses');
      console.error('Error fetching licenses:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentModel]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string, status: string) => {
      setPage(0);
      fetchLicenses(0, rowsPerPage, searchValue, status);
    }, 1000),
    [fetchLicenses, rowsPerPage]
  );

  useEffect(() => {
    fetchLicenses(page, rowsPerPage, searchTerm, filterStatus);
  }, [filterStatus, currentModel]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch(value, filterStatus);
  };

  const handleFilterChange = (event: any) => {
    const value = event.target.value;
    setFilterStatus(value);
    setPage(0);
    fetchLicenses(0, rowsPerPage, searchTerm, value);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    await fetchLicenses(0, newRowsPerPage, searchTerm, filterStatus);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    if (searchTerm) {
      return;
    }
    setPage(newPage);
    await fetchLicenses(newPage, rowsPerPage, searchTerm, filterStatus);
  };

  const handleToggleEnabled = async (serialNo: string, email: string, currentEnabled: boolean, model: ToolENUM) => {
    try {
      const response = await licenseService.updateLicenseStatus(serialNo, email, currentEnabled, model);
      if (response.result) {
        setSuccessMessage('License status updated successfully');
        // Refresh current page
        await fetchLicenses(page, rowsPerPage, searchTerm, filterStatus);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Failed to update license status');
      console.error('Error updating license status:', error);
    }
  };

  const handleCreateLicense = async () => {
    try {
      setCreateLoading(true);
      const response = await licenseService.createLicense(newLicense);
      if (response.result) {
        setSuccessMessage('License created successfully');
        setOpenCreateDialog(false);
        setNewLicense({
          email: '',
          serialNo: '',
          durationInDay: 30,
          durationDayCustom: 0,
          type: LicenseENUM.BASIC,
          dateStart: Date.now(),
          model: currentModel
        });
        // Reset to first page and refresh
        setPage(0);
        await fetchLicenses(0, rowsPerPage, searchTerm, filterStatus);
      } else if (response.message === 'CONFIRM_EXTENSION') {
        // Validate response data structure
        if (response.remainingDays && typeof response.remainingDays === 'number') {
          // Show extension confirmation dialog
          setExtensionDialog({
            open: true,
            remainingDays: response.remainingDays,
            license: newLicense
          });
        } else {
          console.error('Invalid extension data structure:', response);
          setError('Invalid extension confirmation data');
        }
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error in handleCreateLicense:', error);
      setError(error instanceof Error ? error.message : 'Failed to create license');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleConfirmExtension = async () => {
    if (!extensionDialog.license) return;

    try {
      setExtensionLoading(true);
      const response = await licenseService.extendLicense(extensionDialog.license);
      if (response.result) {
        setSuccessMessage('License extended successfully');
        setExtensionDialog({ open: false, remainingDays: 0, license: null });
        setOpenCreateDialog(false);
        setNewLicense({
          email: '',
          serialNo: '',
          durationInDay: 30,
          durationDayCustom: 0,
          type: LicenseENUM.BASIC,
          dateStart: Date.now(),
          model: currentModel
        });
        // Reset to first page and refresh
        setPage(0);
        await fetchLicenses(0, rowsPerPage, searchTerm, filterStatus);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to extend license');
    } finally {
      setExtensionLoading(false);
    }
  };

  const handleEdit = (license: License) => {
    setEditDialog({
      open: true,
      license: {
        email: license.email,
        serialNo: license.serialNo,
        durationInDay: 30,
        durationDayCustom: 0,
        type: license.type,
        dateStart: license.dateStart,
        model: license.model
      }
    });
  };

  const handleEditSuccess = async () => {
    setEditDialog({ open: false, license: null });
    await fetchLicenses(page, rowsPerPage, searchTerm, filterStatus);
  };

  const handleOpenTransfer = (license: License) => {
    setTransferDialog({
      open: true,
      sourceLicense: license,
      destEmail: license.email,
      destSerialNo: ''
    });
  };

  const handleTransfer = async () => {
    if (!transferDialog.sourceLicense || !transferDialog.destEmail || !transferDialog.destSerialNo) return;

    try {
      setTransferLoading(true);
      const response = await licenseService.transferLicenseDays(
        transferDialog.sourceLicense.email,
        transferDialog.sourceLicense.serialNo,
        transferDialog.destEmail,
        transferDialog.destSerialNo,
        currentModel
      );
      if (response.result) {
        setSuccessMessage(response.message);
        setTransferDialog({ open: false, sourceLicense: null, destEmail: '', destSerialNo: '' });
        await fetchLicenses(page, rowsPerPage, searchTerm, filterStatus);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to transfer license days');
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%'}}>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <PageHeader
        eyebrow="Licensing"
        title="Licenses"
        description="Issued licences with their remaining validity, bound serial and duration."
        actions={
          isAdmin ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              disabled={isLoading}
            >
              Add license
            </Button>
          ) : undefined
        }
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ maxWidth: 640 }}>
          <TextField
            sx={{ flexGrow: 1 }}
            placeholder="Search by email or serial number"
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={handleFilterChange}
              disabled={isLoading}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="active">Active</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </PageHeader>

      <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
            <TableCell> </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Serial No</TableCell>
              <TableCell>Remaining Days</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Duration (Days)</TableCell>
              {/* Hidden Start Date column - logic remains unchanged */}
              {/* <TableCell>Start Date</TableCell> */}
              <TableCell>End Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : licenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ p: 0 }}>
                  <EmptyState
                    icon={<VpnKeyOutlinedIcon />}
                    title="No licenses found"
                    description={
                      searchTerm
                        ? 'Nothing matches that email or serial number.'
                        : 'No licences have been issued yet.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              licenses.map((license) => (
                <TableRow key={`${license.email}-${license.serialNo}`} hover>
                  <TableCell>
                    {isAdmin && (
                      <>
                        <Tooltip title="Edit License">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(license)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Transfer Days">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTransfer(license)}
                            color="secondary"
                            disabled={Math.ceil((license.dateEnd - Date.now()) / (1000 * 60 * 60 * 24)) <= 0}
                          >
                            <SwapHorizIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{license.email}</TableCell>
                  <TableCell className="mono">{license.serialNo}</TableCell>
                  <TableCell>
                    {(() => {
                      const remainingDays = Math.max(0, Math.ceil((license.dateEnd - Date.now()) / (1000 * 60 * 60 * 24)));
                      if (remainingDays <= 0) {
                        return <StatusChip label="Expired" tone="error" />;
                      }
                      // A licence inside its last week is the one an operator
                      // needs to spot while scanning the column.
                      return (
                        <StatusChip
                          label={`${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`}
                          tone={remainingDays <= 7 ? 'warning' : 'success'}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!license.isDisabled}
                      onChange={() => handleToggleEnabled(license.serialNo, license.email, !license.isDisabled, license.model)}
                      disabled={!isAdmin}
                    />
                  </TableCell>
                  <TableCell>{license.type}</TableCell>
                  <TableCell>{license.durationInDay}</TableCell>
                  {/* Hidden Start Date data - logic remains unchanged */}
                  {/* <TableCell>{new Date(license.dateStart).toLocaleString()}</TableCell> */}
                  <TableCell>{new Date(license.dateEnd).toLocaleString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={searchTerm ? licenses.length : totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        nextIconButtonProps={{
          disabled: page >= totalPages - 1 || isLoading || !!searchTerm
        }}
        backIconButtonProps={{
          disabled: page === 0 || isLoading || !!searchTerm
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
      />
      </Card>

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New License</DialogTitle>
        <DialogContent>
          <CreateLicenseForm
            license={newLicense}
            onLicenseChange={setNewLicense}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLicense}
            variant="contained"
            color="primary"
            disabled={!newLicense.email || !newLicense.serialNo || createLoading}
          >
            {createLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Extension Confirmation Dialog */}
      <Dialog
        open={extensionDialog.open}
        onClose={() => setExtensionDialog({ open: false, remainingDays: 0, license: null })}
      >
        <DialogTitle>Confirm License Extension</DialogTitle>
        <DialogContent>
          <Typography>
            Device còn {extensionDialog.remainingDays} ngày active, thực hiện cộng dồn không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtensionDialog({ open: false, remainingDays: 0, license: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmExtension} variant="contained" color="primary" disabled={extensionLoading}>
            {extensionLoading ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit License Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, license: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit License</DialogTitle>
        <DialogContent>
          {editDialog.license && (
            <CreateLicenseForm
              license={editDialog.license}
              onLicenseChange={(updatedLicense) => {
                setEditDialog(prev => ({
                  ...prev,
                  license: updatedLicense
                }));
              }}
              disabledFields={{ email: true, serialNo: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, license: null })}>Cancel</Button>
          <Button
            onClick={async () => {
              if (editDialog.license) {
                try {
                  setEditLoading(true);
                  const response = await licenseService.extendLicense(editDialog.license);
                  if (response.result) {
                    setSuccessMessage('License updated successfully');
                    handleEditSuccess();
                  } else {
                    setError(response.message);
                  }
                } catch (error) {
                  setError('Failed to update license');
                  console.error('Error updating license:', error);
                } finally {
                  setEditLoading(false);
                }
              }
            }}
            variant="contained"
            color="primary"
            disabled={editLoading}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer License Days Dialog */}
      <Dialog
        open={transferDialog.open}
        onClose={() => setTransferDialog({ open: false, sourceLicense: null, destEmail: '', destSerialNo: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Remaining Days</DialogTitle>
        <DialogContent>
          {transferDialog.sourceLicense && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Source License
              </Typography>
              <TextField
                label="Source Email"
                value={transferDialog.sourceLicense.email}
                disabled
                fullWidth
                size="small"
              />
              <TextField
                label="Source Serial No"
                value={transferDialog.sourceLicense.serialNo}
                disabled
                fullWidth
                size="small"
              />
              <TextField
                label="Remaining Days"
                value={Math.max(0, Math.ceil((transferDialog.sourceLicense.dateEnd - Date.now()) / (1000 * 60 * 60 * 24)))}
                disabled
                fullWidth
                size="small"
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Destination License
              </Typography>
              <TextField
                label="Destination Email"
                value={transferDialog.destEmail}
                onChange={(e) => setTransferDialog(prev => ({ ...prev, destEmail: e.target.value }))}
                fullWidth
                size="small"
                placeholder="Enter destination email"
              />
              <TextField
                label="Destination Serial No"
                value={transferDialog.destSerialNo}
                onChange={(e) => setTransferDialog(prev => ({ ...prev, destSerialNo: e.target.value }))}
                fullWidth
                size="small"
                placeholder="Enter destination serial number"
              />
              <Alert severity="warning" sx={{ mt: 1 }}>
                Toàn bộ ngày còn lại sẽ được chuyển sang device đích. device nguồn sẽ hết hạn ngay lập tức.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog({ open: false, sourceLicense: null, destEmail: '', destSerialNo: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            variant="contained"
            color="primary"
            disabled={transferLoading || !transferDialog.destEmail || !transferDialog.destSerialNo}
          >
            {transferLoading ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};